import {
  MilesExpirationDate,
  PaymentMethod,
  sellSubmissionFormEventDataSchema,
} from "@/core";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { z } from "zod";

const sellFormDataSchema = sellSubmissionFormEventDataSchema.shape.data
  .omit({ miles: true, averageRate: true, totalPayout: true })
  .extend({
    paypalEmail: z.string().optional(),
    agreedToTerms: z.boolean().refine((val) => val === true, {
      message: "You must agree to the terms and conditions",
    }),
  })
  .refine((data) => data.paymentMethod !== undefined, {
    message: "Payment method is required",
    path: ["paymentMethod"],
  });

export type SellFormData = z.infer<typeof sellFormDataSchema>;

export function useSellForm(
  defaultValues: Partial<SellFormData> = {},
): UseFormReturn<SellFormData> {
  const form = useForm<SellFormData>({
    resolver: zodResolver(sellFormDataSchema),
    mode: "onChange", // Enable validation on change
    reValidateMode: "onChange", // Re-validate on change after initial validation
    defaultValues: {
      frequentFlyerNumber: defaultValues.frequentFlyerNumber || "",
      accountPassword: defaultValues.accountPassword || "",
      accountEmail: defaultValues.accountEmail || "",
      milesExpirationDate:
        defaultValues.milesExpirationDate || MilesExpirationDate.NEVER,
      accountPhoneNumber: defaultValues.accountPhoneNumber || "",
      paymentMethod: defaultValues.paymentMethod ?? PaymentMethod.PAYPAL,
      paypalEmail: defaultValues.paypalEmail || "",
      notes: defaultValues.notes || "",
      agreedToTerms: false,
    },
  });

  return form;
}

export function useHydrateSellFormContactDetails({
  customerEmail,
  customerPhoneNumber,
  sameAsEmail,
  setValue,
}: {
  customerEmail: string;
  customerPhoneNumber: string;
  sameAsEmail: boolean;
  setValue: UseFormReturn<SellFormData>["setValue"];
}) {
  useEffect(() => {
    if (customerEmail) {
      setValue("accountEmail", customerEmail, {
        shouldDirty: false,
        shouldValidate: sameAsEmail,
      });
    }

    if (customerPhoneNumber) {
      setValue("accountPhoneNumber", customerPhoneNumber, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [customerEmail, customerPhoneNumber, sameAsEmail, setValue]);
}
